import type { Pose } from '@/components/riffle/types'

// ─── §8 Math helpers (reproduce verbatim) ───────────────────

export const DEG = Math.PI / 180

export const clamp = (v: number, a: number, b: number): number =>
    Math.min(b, Math.max(a, v))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

// smoothstep: t²(3 − 2t), clamp t to [0,1] first
export const smoothstep = (t: number): number => {
    const c = clamp(t, 0, 1)
    return c * c * (3 - 2 * c)
}

// smoother: Perlin quintic t³(t(6t − 15) + 10), clamp first
export const smoother = (t: number): number => {
    const c = clamp(t, 0, 1)
    return c * c * c * (c * (6 * c - 15) + 10)
}

// easeInOut cubic, clamp first
export const easeInOut = (t: number): number => {
    const c = clamp(t, 0, 1)
    return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2
}

// Deterministic hash: frac(sin(seed·12.9898 + 78.233) · 43758.5453)
export const rand = (seed: number): number => {
    const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453
    return x - Math.floor(x)
}

// Blend two poses field-by-field.
export const lerpPose = (a: Pose, b: Pose, t: number): Pose => ({
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    z: lerp(a.z, b.z, t),
    rx: lerp(a.rx, b.rx, t),
    ry: lerp(a.ry, b.ry, t),
    rz: lerp(a.rz, b.rz, t),
    s: lerp(a.s, b.s, t),
    o: lerp(a.o, b.o, t)
})
